// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
'use client';
/**
 * Un SEGMENT : onglets de vue, filtres à choix unique, bascule entre deux modes.
 * Règles : `shared/docs/UI-CONVENTIONS.md`. Apparence : `segmentStyles.ts`.
 *
 *     <Segment ariaLabel="Période" valeur={periode} onChange={setPeriode}
 *       options={[{ valeur: 'mois', libelle: 'Mois' }, { valeur: 'semaine', libelle: 'Semaine' }]} />
 *
 * Ce n'est pas un `Bouton` : choisir une vue n'est pas une action, et l'option active n'est pas un
 * bouton plein (elle ne doit pas concurrencer « Enregistrer »). `ariaLabel` est OBLIGATOIRE : un
 * groupe de boutons sans nom ne dit pas de quoi il est le choix.
 */
import React from 'react';
import type { TailleBouton } from './boutonStyles';
import { styleSegmentOption, styleSegmentRail } from './segmentStyles';

export type OptionSegment<T extends string | number> = {
  valeur: T;
  libelle: React.ReactNode;
  /** Infobulle. */
  aide?: string;
  disabled?: boolean;
  /** Pastille de compte à droite du libellé (« À traiter 3 »). */
  compteur?: number;
};

export type SegmentProps<T extends string | number> = {
  options: readonly OptionSegment<T>[];
  valeur: T;
  onChange: (valeur: T) => void;
  /** Nom du groupe, lu par les lecteurs d'écran (« Période », « Vue »). */
  ariaLabel: string;
  taille?: TailleBouton;
  /** Occupe toute la largeur et répartit les options à parts égales. */
  pleineLargeur?: boolean;
  style?: React.CSSProperties;
};

const FEUILLE = `
.bio-seg-opt { transition: background var(--dur-fast, 120ms) ease-out, color var(--dur-fast, 120ms) ease-out; }
.bio-seg-opt:not(:disabled):not([aria-pressed="true"]):hover { background: var(--gray-100) !important; color: var(--text-strong) !important; }
.bio-seg-opt:disabled { opacity: 0.45; cursor: not-allowed; }
@media (prefers-reduced-motion: reduce) { .bio-seg-opt { transition: none; } }
`;

export function Segment<T extends string | number>({
  options, valeur, onChange, ariaLabel, taille = 'sm', pleineLargeur = false, style,
}: SegmentProps<T>) {
  return (
    <>
      <style href="bio-segment" precedence="default">{FEUILLE}</style>
      <div role="group" aria-label={ariaLabel} style={{ ...styleSegmentRail(pleineLargeur), ...style }}>
        {options.map((o) => {
          const actif = o.valeur === valeur;
          return (
            <button
              key={String(o.valeur)} type="button" className="bio-seg-opt" aria-pressed={actif}
              disabled={o.disabled} title={o.aide} onClick={() => { if (!actif) onChange(o.valeur); }}
              style={styleSegmentOption(actif, taille, pleineLargeur)}
            >
              {o.libelle}
              {typeof o.compteur === 'number' && o.compteur > 0 && (
                <span style={{ fontSize: 11, fontWeight: 700, padding: '0 6px', borderRadius: 'var(--radius-pill, 999px)', background: 'var(--gray-100)', color: 'var(--text-body)' }}>
                  {o.compteur}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}
